import React, { useEffect, useMemo, useState } from "react"
import { Modal } from 'react-bootstrap'

import { formatDate, formatMoney } from 'helpers/helpers'
import { httpOk, GET, POST } from 'helpers/api'
import { telegramClientMessage } from 'pages/parts/ModalPosTelegramLink'

// Считаем по брендам, а не по точкам: группа привязывается к login,
// и /pay из неё работает по всем точкам бренда сразу.
function ModalPosTelegramStats({ show, onHide }) {
	const [data, setData] = useState(null)
	const [tab, setTab] = useState('missing')
	const [search, setSearch] = useState('')
	const [link, setLink] = useState(null)
	const [copied, setCopied] = useState('')

	async function getData() {
		const response = await GET('/admin/pos-telegram-chats', {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	// Код выписывается на бренд точки, а не на того, кто нажал кнопку:
	// поддержке нужна ссылка для клиента, а не для своего логина.
	async function createLink(item) {
		const response = await POST('/admin/pos-telegram-link', {
			pos_id: item.first_pos_id,
		}, { loader: true, notification: false })
		if (httpOk(response)) {
			setLink({ ...response.data, brand_name: item.brand_name })
			setCopied('')
		}
	}

	function copyText(text, mark) {
		navigator.clipboard.writeText(text)
		setCopied(mark)
	}

	useEffect(() => {
		if (show) {
			setTab('missing')
			setSearch('')
			setLink(null)
			getData()
		} else {
			setData(null)
		}
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	const rows = useMemo(() => {
		const list = (tab === 'missing' ? data?.missing : data?.connected) || []
		const needle = search.trim().toLowerCase()
		if (!needle) return list
		return list.filter(item =>
			String(item.login || '').toLowerCase().includes(needle) ||
			String(item.brand_name || '').toLowerCase().includes(needle) ||
			String(item.region_name || '').toLowerCase().includes(needle) ||
			String(item.chat_title || '').toLowerCase().includes(needle)
		)
	}, [data, tab, search])

	const summary = data?.summary
	const connected = tab === 'connected'

	return (
		<Modal show={show} animation={false} centered
			dialogClassName="modal-telegram-chats"
			onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>Telegram-группы — @{data?.bot || 'mison_manager_bot'}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="d-flex flex-wrap align-items-center gap-2 mb-3" style={{ fontSize: 13 }}>
					<span className="bg-success bg-border text-white">
						<span>Бренды: {summary?.brands_connected ?? 0} / {summary?.brands_total ?? 0}</span>
					</span>
					<span className="bg-success bg-border text-white">
						<span>Точки: {summary?.poses_connected ?? 0} / {summary?.poses_total ?? 0}</span>
					</span>
					<span className="bg-danger bg-border text-white">
						<span>Не подключено: {summary?.brands_missing ?? 0}</span>
					</span>
					<span className="text-muted ms-auto">
						Групп в системе: {summary?.chats_total ?? 0}
					</span>
				</div>

				{/* бот сидит в куда большем числе групп, чем привязано: без привязки
					система не знает, чей это бренд, и /pay из группы не сработает */}
				<div className="text-muted mb-2" style={{ fontSize: 12 }}>
					Подключён — значит группа привязана к бренду. Бот может уже сидеть в группе,
					но пока привязки нет, счета оттуда не выставляются.
				</div>

				{link &&
					<div className="telegram-token-box mb-2" style={{ fontSize: 13 }}>
						<div className="d-flex flex-wrap align-items-center gap-2">
							<b>{link.brand_name}</b>
							<span className="text-muted">
								код действует до {formatDate(link.expires_at, 'dd.MM.yyyy HH:mm')}
							</span>
							<button type="button" className="btn btn-sm btn-light ms-auto"
								onClick={() => setLink(null)}>
								Скрыть
							</button>
						</div>
						<div className="d-flex flex-wrap align-items-center gap-2 mt-2">
							<input type="text" readOnly className="form-control form-control-sm"
								style={{ flex: '1 1 420px' }} value={link.link} />
							<button type="button" className="btn btn-sm btn-primary"
								onClick={() => copyText(telegramClientMessage(link), 'message')}>
								{copied === 'message' ? 'Скопировано' : 'Копировать сообщение клиенту'}
							</button>
							<button type="button" className="btn btn-sm btn-outline-primary"
								onClick={() => copyText(link.link, 'link')}>
								{copied === 'link' ? 'Скопировано' : 'Только ссылка'}
							</button>
						</div>
						<div className="text-muted mt-2">
							В сообщении обе инструкции сразу: по ссылке бот войдёт в выбранную группу сам,
							а если он там уже сидит — сработает команда.
						</div>
					</div>
				}

				<div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
					<div className="btn-group">
						<button type="button"
							className={`btn btn-sm ${!connected ? 'btn-primary' : 'btn-outline-primary'}`}
							onClick={() => setTab('missing')}>
							Не подключены ({data?.missing?.length ?? 0})
						</button>
						<button type="button"
							className={`btn btn-sm ${connected ? 'btn-primary' : 'btn-outline-primary'}`}
							onClick={() => setTab('connected')}>
							Подключены ({data?.connected?.length ?? 0})
						</button>
					</div>
					<input type="text"
						className="form-control form-control-sm"
						style={{ maxWidth: 280 }}
						placeholder="Поиск по логину, бренду, региону"
						value={search}
						onChange={(e) => setSearch(e.target.value)} />
				</div>

				{/* высота фиксированная, а не по содержимому: иначе при поиске
					таблица схлопывается и модалка прыгает под курсором */}
				<div className="table-responsive" style={{ height: 480, maxHeight: '60vh', overflowY: 'auto' }}>
					<table className="table table-bordered table-hover mb-0" style={{ fontSize: 13 }}>
						<thead style={{ position: 'sticky', top: 0, background: 'var(--dark-surface, #fff)', zIndex: 1 }}>
							<tr>
								<th style={{ width: 40 }} className="text-center">#</th>
								<th>Логин</th>
								<th>Бренд</th>
								<th>Регион</th>
								<th className="text-center" style={{ width: 70 }}>Точек</th>
								<th className="text-end text-nowrap">Тариф</th>
								<th className="text-end text-nowrap">Баланс</th>
								{!connected && <th className="text-nowrap">Телефон</th>}
								{!connected && <th className="text-nowrap" style={{ width: 110 }}>Оплачен до</th>}
								{!connected && <th style={{ width: 110 }}></th>}
								{connected && <th>Группа</th>}
								{connected && <th className="text-nowrap" style={{ width: 110 }}>Подключена</th>}
							</tr>
						</thead>
						<tbody>
							{rows.map((item, index) => (
								<tr key={item.login}>
									<td className="text-center">{index + 1}</td>
									<td className="text-nowrap">{item.login}</td>
									<td>{item.brand_name}</td>
									<td>{item.region_name || '—'}</td>
									<td className="text-center">{item.poses}</td>
									<td className="text-end text-nowrap">{formatMoney(item.tariff_total || 0)}</td>
									<td className={`text-end text-nowrap ${Number(item.balance_total) < 0 ? 'text-danger' : ''}`}>
										{formatMoney(item.balance_total || 0)}
									</td>
									{!connected && <td className="text-nowrap">{item.phone || '—'}</td>}
									{!connected &&
										<td className="text-nowrap">
											{item.paid_until_at ? formatDate(item.paid_until_at, 'dd.MM.yyyy') : '—'}
										</td>
									}
									{!connected &&
										<td className="text-center">
											<button type="button"
												className="btn btn-sm btn-outline-primary"
												onClick={() => createLink(item)}>
												Ссылка
											</button>
										</td>
									}
									{connected &&
										<td>
											{item.chat_title || item.chat_id}
											{item.chats_count > 1 &&
												<span className="text-muted"> (+{item.chats_count - 1})</span>
											}
										</td>
									}
									{connected &&
										<td className="text-nowrap">{formatDate(item.connected_at, 'dd.MM.yyyy')}</td>
									}
								</tr>
							))}
							{rows.length === 0 &&
								<tr>
									<td colSpan={connected ? 9 : 10} className="text-center text-muted">
										Ничего не найдено
									</td>
								</tr>
							}
						</tbody>
					</table>
				</div>
			</Modal.Body>
		</Modal>
	)
}

export default ModalPosTelegramStats
